import { z } from 'zod';
import {
  isValidBrazilianPhone,
  isValidCnpj,
  CNPJ_MASK_REGEX,
} from '@/lib/validators';

export type InterestDataType = {
  name: string;
  email: string;
  phone: string;
  cnpj: string;
  page: string;
  origemContato: OrigemContato;
  enviarKonecta?: boolean;
};

const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ'’\- ]+$/;

/**
 * Opções do campo "Como nos conheceu?".
 *
 * O valor enviado para a API é a própria string (ex: "Agência CAIXA"),
 * por isso value e label coincidem no select.
 */
export const ORIGEM_CONTATO_OPTIONS = [
  'Google',
  'Instagram',
  'Agência CAIXA',
  'Indicação',
  'Outros',
] as const;

export type OrigemContato = (typeof ORIGEM_CONTATO_OPTIONS)[number];

export const interestDataSchema = z.object({
  name: z
    .string({ required_error: 'Nome é obrigatório' })
    .trim()
    .min(3, { message: 'Nome deve ter pelo menos 3 caracteres' })
    .max(100, { message: 'Nome não pode ter mais de 100 caracteres' })
    .regex(NAME_REGEX, { message: 'Nome deve conter apenas letras' })
    .refine((val) => val.trim().split(/\s+/).length >= 2, {
      message: 'Informe o nome completo',
    }),

  email: z
    .string({ required_error: 'Email é obrigatório' })
    .trim()
    .toLowerCase()
    .min(5, { message: 'Email deve ter pelo menos 5 caracteres' })
    .max(100, { message: 'Email não pode ter mais de 100 caracteres' })
    .email({ message: 'Email inválido' })
    .refine((val) => !/\.\./.test(val), { message: 'Email inválido' })
    .refine(
      (val) => {
        const domain = val.split('@')[1] ?? '';
        return domain.includes('.') && domain.length >= 4;
      },
      { message: 'Email inválido' }
    ),

  phone: z
    .string({ required_error: 'Telefone é obrigatório' })
    .min(15, { message: 'Informe um celular com DDD (11 dígitos)' })
    .max(15, { message: 'Telefone não pode ter mais de 15 caracteres' })
    .refine(isValidBrazilianPhone, {
      message: 'Informe um celular válido com 9 após o DDD',
    }),

  cnpj: z
    .string({ required_error: 'CNPJ é obrigatório' })
    .regex(CNPJ_MASK_REGEX, 'Formato de CNPJ inválido')
    .refine(isValidCnpj, { message: 'CNPJ inválido' }),

  page: z.string().min(1, 'Página é obrigatória'),

  origemContato: z.enum(ORIGEM_CONTATO_OPTIONS, {
    required_error: 'Informe como nos conheceu',
    invalid_type_error: 'Informe como nos conheceu',
  }),
});

/**
 * Server-side validation schema.
 *
 * Mirrors the security constraints of `interestDataSchema`. Client-side
 * validation can always be bypassed (direct POST to the server action,
 * disabled JS, intercepted request), so every field — especially the
 * free-text `name` — must be revalidated here to block XSS / SQL injection
 * payloads.
 */
export const interestDataServerSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3)
    .max(100)
    .regex(NAME_REGEX)
    .refine((val) => val.trim().split(/\s+/).length >= 2),

  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(5)
    .max(100)
    .email()
    .refine((val) => !/\.\./.test(val))
    .refine((val) => {
      const domain = val.split('@')[1] ?? '';
      return domain.includes('.') && domain.length >= 4;
    }),

  phone: z.string().min(15).max(15).refine(isValidBrazilianPhone),

  cnpj: z.string().regex(CNPJ_MASK_REGEX).refine(isValidCnpj),

  page: z.string().min(1).max(50),

  origemContato: z.enum(ORIGEM_CONTATO_OPTIONS),

  enviarKonecta: z.boolean().optional(),
});
